import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import { Archive, ArchiveRestore, Building2, Edit, Plus, Trash2 } from 'lucide-react';
import type { InsertRetailer } from '@shared/schema';
import { getJson } from '@/lib/http';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RetailerWorkspaceProvider } from './RetailerWorkspaceContext';
import { RetailerWorkspaceShell } from './RetailerWorkspaceShell';
import {
  DEFAULT_RETAILER_SECTION,
  RETAILER_MANAGEMENT_BASE,
  getSectionAccessDenial,
  isRetailerSectionId,
  retailerWorkspacePath,
  type RetailerSectionId,
} from './sections';
import type { RetailerWithFormats } from './types';
import { useRetailerWorkspace } from './RetailerWorkspaceContext';

function UnsavedWorkspaceNavigationDialog() {
  const {
    pendingNavigation,
    confirmPendingNavigation,
    cancelPendingNavigation,
  } = useRetailerWorkspace();

  return (
    <AlertDialog
      open={pendingNavigation != null}
      onOpenChange={(open) => {
        if (!open) cancelPendingNavigation();
      }}
    >
      <AlertDialogContent className={cn('bg-white')}>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes in this retailer workspace. Leave this section and
            discard them, or stay and keep editing.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={cancelPendingNavigation}>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={confirmPendingNavigation}>
            Discard and continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function RetailerManagementPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const [workspaceMatch, workspaceParams] = useRoute(
    `${RETAILER_MANAGEMENT_BASE}/:retailerId/:section`,
  );
  const [retailerOnlyMatch, retailerOnlyParams] = useRoute(
    `${RETAILER_MANAGEMENT_BASE}/:retailerId`,
  );

  const routeRetailerId = workspaceMatch
    ? Number(workspaceParams?.retailerId)
    : retailerOnlyMatch
      ? Number(retailerOnlyParams?.retailerId)
      : null;

  const routeSection: RetailerSectionId = workspaceMatch
    && isRetailerSectionId(workspaceParams?.section)
    ? workspaceParams.section
    : DEFAULT_RETAILER_SECTION;

  const [showArchivedRetailers, setShowArchivedRetailers] = useState(false);
  const [isRetailerDialogOpen, setIsRetailerDialogOpen] = useState(false);
  const [isEditRetailerDialogOpen, setIsEditRetailerDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [retailerFormData, setRetailerFormData] = useState<
    Partial<InsertRetailer> & { logoFile?: File }
  >({});
  const [editingRetailer, setEditingRetailer] = useState<RetailerWithFormats | null>(null);
  const [retailerToDelete, setRetailerToDelete] = useState<RetailerWithFormats | null>(null);
  const [retailerToArchive, setRetailerToArchive] = useState<RetailerWithFormats | null>(null);
  const [accessNotice, setAccessNotice] = useState<string | null>(null);

  const includeArchivedInQuery =
    showArchivedRetailers || Boolean(routeRetailerId && !Number.isNaN(routeRetailerId));

  const {
    data: retailers = [],
    isLoading,
    isError,
  } = useQuery<RetailerWithFormats[]>({
    queryKey: ['/api/retailers', includeArchivedInQuery],
    queryFn: async () => {
      const url = includeArchivedInQuery
        ? '/api/retailers?includeArchived=true'
        : '/api/retailers';
      const data = await getJson<RetailerWithFormats[]>(url);
      return Array.isArray(data) ? data : [];
    },
  });

  const directoryRetailers = useMemo(
    () =>
      showArchivedRetailers
        ? retailers
        : retailers.filter((retailer) => !retailer.archivedAt),
    [retailers, showArchivedRetailers],
  );

  const activeRetailer = useMemo(
    () => retailers.find((retailer) => retailer.id === routeRetailerId) ?? null,
    [retailers, routeRetailerId],
  );

  const navigateToRetailer = useCallback(
    (retailerId: number, section: RetailerSectionId = routeSection) => {
      setLocation(retailerWorkspacePath(retailerId, section));
    },
    [routeSection, setLocation],
  );

  const navigateToSection = useCallback(
    (section: RetailerSectionId) => {
      if (!routeRetailerId) return;
      setLocation(retailerWorkspacePath(routeRetailerId, section));
    },
    [routeRetailerId, setLocation],
  );

  const navigateToDirectory = useCallback(() => {
    setLocation(RETAILER_MANAGEMENT_BASE);
  }, [setLocation]);

  useEffect(() => {
    if (retailerOnlyMatch && !workspaceMatch && routeRetailerId && !Number.isNaN(routeRetailerId)) {
      setLocation(retailerWorkspacePath(routeRetailerId, DEFAULT_RETAILER_SECTION));
      return;
    }

    if (
      workspaceMatch
      && workspaceParams?.section
      && !isRetailerSectionId(workspaceParams.section)
      && routeRetailerId
      && !Number.isNaN(routeRetailerId)
    ) {
      setAccessNotice(
        `“${workspaceParams.section}” is not a valid workspace section. Showing Overview instead.`,
      );
      setLocation(retailerWorkspacePath(routeRetailerId, DEFAULT_RETAILER_SECTION));
    }
  }, [
    retailerOnlyMatch,
    workspaceMatch,
    routeRetailerId,
    workspaceParams?.section,
    setLocation,
  ]);

  useEffect(() => {
    if (!workspaceMatch || !activeRetailer || !routeRetailerId || Number.isNaN(routeRetailerId)) {
      return;
    }
    if (!isRetailerSectionId(workspaceParams?.section)) return;

    const denial = getSectionAccessDenial(activeRetailer, routeSection);
    if (!denial) return;

    setAccessNotice(denial);
    if (routeSection !== DEFAULT_RETAILER_SECTION) {
      setLocation(retailerWorkspacePath(routeRetailerId, DEFAULT_RETAILER_SECTION));
    }
  }, [
    workspaceMatch,
    workspaceParams?.section,
    activeRetailer,
    routeRetailerId,
    routeSection,
    setLocation,
  ]);

  const createRetailerMutation = useMutation({
    mutationFn: async (data: InsertRetailer & { logoFile?: File }) => {
      const response = await apiRequest('/api/retailers', 'POST', {
        name: data.name,
        description: data.description,
      });
      const retailer = await response.json();

      if (data.logoFile && retailer.id) {
        const formData = new FormData();
        formData.append('logo', data.logoFile);
        const token = localStorage.getItem('sb_jwt_token') || '';
        const logoResponse = await fetch(`/api/retailers/${retailer.id}/logo`, {
          method: 'POST',
          body: formData,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!logoResponse.ok) {
          const errorData = await logoResponse.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.detail || 'Failed to upload logo');
        }
        return await logoResponse.json();
      }

      return retailer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/retailers'] });
      setIsRetailerDialogOpen(false);
      setRetailerFormData({});
      toast({ title: 'Success', description: 'Retailer created successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create retailer',
        variant: 'destructive',
      });
    },
    retry: false,
  });

  const updateRetailerMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<InsertRetailer> & { logoFile?: File };
    }) => {
      const updatedRetailer = await apiRequest(`/api/retailers/${id}`, 'PATCH', {
        name: data.name,
        description: data.description,
      });

      if (data.logoFile) {
        const formData = new FormData();
        formData.append('logo', data.logoFile);
        const response = await fetch(`/api/retailers/${id}/logo`, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          throw new Error('Failed to upload logo');
        }
        return await response.json();
      }

      return updatedRetailer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/retailers'] });
      setIsEditRetailerDialogOpen(false);
      setEditingRetailer(null);
      setRetailerFormData({});
      toast({ title: 'Success', description: 'Retailer updated successfully' });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update retailer',
        variant: 'destructive',
      });
    },
  });

  const deleteRetailerMutation = useMutation({
    mutationFn: async (id: number) => apiRequest(`/api/retailers/${id}`, 'DELETE'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/api/retailers'], refetchType: 'active' });
      setIsDeleteDialogOpen(false);
      setRetailerToDelete(null);
      navigateToDirectory();
      toast({ title: 'Success', description: 'Retailer deleted successfully' });
    },
    onError: (error: any) => {
      const errorData = error.data || {};
      let description = errorData.message || error.message || 'Failed to delete retailer';
      if (errorData.details) description += `\n\nDependencies: ${errorData.details}`;
      if (errorData.hint) description += `\n\n${errorData.hint}`;
      toast({
        title: 'Cannot Delete Retailer',
        description,
        variant: 'destructive',
        duration: 10000,
      });
    },
  });

  const archiveRetailerMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: number; archive: boolean }) =>
      apiRequest(`/api/retailers/${id}/archive`, 'PATCH', { archive }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/retailers'] });
      setIsArchiveDialogOpen(false);
      setRetailerToArchive(null);
      setIsEditRetailerDialogOpen(false);
      setEditingRetailer(null);
      toast({
        title: 'Success',
        description: variables.archive
          ? 'Retailer archived successfully'
          : 'Retailer restored successfully',
      });
    },
    onError: (error: any) => {
      const message = error?.data?.error || error?.message || 'Failed to archive retailer';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const openEditRetailer = (retailer: RetailerWithFormats) => {
    setEditingRetailer(retailer);
    setRetailerFormData({
      name: retailer.name,
      description: retailer.description,
      logo: retailer.logo,
    });
    setIsEditRetailerDialogOpen(true);
  };

  const handleCreateRetailer = () => {
    if (!retailerFormData.name) {
      toast({
        title: 'Error',
        description: 'Retailer name is required',
        variant: 'destructive',
      });
      return;
    }
    createRetailerMutation.mutate(retailerFormData as InsertRetailer);
  };

  const handleUpdateRetailer = () => {
    if (!retailerFormData.name || !editingRetailer) {
      toast({
        title: 'Error',
        description: 'Retailer name is required',
        variant: 'destructive',
      });
      return;
    }
    updateRetailerMutation.mutate({
      id: editingRetailer.id,
      data: retailerFormData,
    });
  };

  const workspaceValue = useMemo(
    () => ({
      mode: (routeRetailerId ? 'retailer' : 'platform') as 'platform' | 'retailer',
      // Selector prefers the visible directory set; include full fetch so an
      // archived deep-link remains selectable while in that workspace.
      retailers: showArchivedRetailers || activeRetailer?.archivedAt
        ? retailers
        : directoryRetailers,
      activeRetailer,
      activeRetailerId: routeRetailerId && !Number.isNaN(routeRetailerId) ? routeRetailerId : null,
      activeSection: routeSection,
      showArchivedRetailers,
      setShowArchivedRetailers,
      navigateToRetailer,
      navigateToSection,
      navigateToDirectory,
    }),
    [
      routeRetailerId,
      showArchivedRetailers,
      activeRetailer,
      retailers,
      directoryRetailers,
      routeSection,
      navigateToRetailer,
      navigateToSection,
      navigateToDirectory,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-lg">Loading retailers...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-red-600">Error loading retailers. Please refresh the page.</div>
      </div>
    );
  }

  const inWorkspace = Boolean(routeRetailerId && !Number.isNaN(routeRetailerId));

  return (
    <RetailerWorkspaceProvider value={workspaceValue}>
      <div className="container mx-auto space-y-6 p-6">
        {!inWorkspace && (
          <PageHeader
            icon={Building2}
            title="Retailer Management"
            description="Choose a retailer to open its workspace"
          >
            <Dialog open={isRetailerDialogOpen} onOpenChange={setIsRetailerDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Retailer
                </Button>
              </DialogTrigger>
            </Dialog>
          </PageHeader>
        )}

        {!inWorkspace && (
          <>
            <div className="mb-4 flex items-center space-x-2">
              <Checkbox
                id="show-archived-retailers"
                checked={showArchivedRetailers}
                onCheckedChange={(checked) => setShowArchivedRetailers(!!checked)}
              />
              <label
                htmlFor="show-archived-retailers"
                className="cursor-pointer text-sm font-medium leading-none"
              >
                Show Archived Retailers
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {directoryRetailers.map((retailer) => (
                <Card
                  key={retailer.id}
                  className={cn(
                    'transition-colors hover:bg-muted/30',
                    retailer.archivedAt && 'opacity-60',
                  )}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-start gap-3 text-left"
                        onClick={() => navigateToRetailer(retailer.id, DEFAULT_RETAILER_SECTION)}
                      >
                        {retailer.logo ? (
                          <img
                            src={retailer.logo}
                            alt={`${retailer.name} logo`}
                            className="h-10 w-10 rounded border bg-white object-contain p-1"
                          />
                        ) : (
                          <Building2 className="mt-1 h-6 w-6 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <CardTitle className="truncate">{retailer.name}</CardTitle>
                          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                            {retailer.description || 'No description'}
                          </p>
                        </div>
                      </button>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditRetailer(retailer)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRetailerToDelete(retailer);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-wrap items-center gap-2">
                    {retailer.archivedAt && (
                      <Badge
                        variant="destructive"
                        className="border-amber-300 bg-amber-100 text-amber-800"
                      >
                        Archived
                      </Badge>
                    )}
                    <Button
                      className="ml-auto"
                      size="sm"
                      onClick={() => navigateToRetailer(retailer.id, DEFAULT_RETAILER_SECTION)}
                    >
                      Open workspace
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {directoryRetailers.length === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Building2 className="mb-4 h-16 w-16 text-muted-foreground" />
                  <h3 className="mb-2 text-xl font-semibold">No Retailers</h3>
                  <p className="mb-4 text-center text-muted-foreground">
                    Get started by creating your first retailer network
                  </p>
                  <Button onClick={() => setIsRetailerDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Retailer
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {inWorkspace && (
          <RetailerWorkspaceShell
            accessNotice={accessNotice}
            onDismissAccessNotice={() => setAccessNotice(null)}
            onEditRetailer={() => {
              if (activeRetailer) openEditRetailer(activeRetailer);
            }}
            onArchiveRetailer={() => {
              if (activeRetailer) {
                setRetailerToArchive(activeRetailer);
                setIsArchiveDialogOpen(true);
              }
            }}
          />
        )}

        <UnsavedWorkspaceNavigationDialog />

        <Dialog open={isRetailerDialogOpen} onOpenChange={setIsRetailerDialogOpen}>
          <DialogContent className={cn('bg-white')}>
            <DialogHeader>
              <DialogTitle>Create New Retailer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Retailer Name</Label>
                <Input
                  id="name"
                  value={retailerFormData.name || ''}
                  onChange={(e) =>
                    setRetailerFormData({ ...retailerFormData, name: e.target.value })
                  }
                  placeholder="e.g., Dunnes Stores"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={retailerFormData.description || ''}
                  onChange={(e) =>
                    setRetailerFormData({ ...retailerFormData, description: e.target.value })
                  }
                  placeholder="Brief description of the retailer"
                />
              </div>
              <div>
                <Label htmlFor="logo">Logo</Label>
                <Input
                  id="logo"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) {
                      toast({
                        title: 'File too large',
                        description: 'Logo must be under 2MB',
                        variant: 'destructive',
                      });
                      return;
                    }
                    setRetailerFormData({ ...retailerFormData, logoFile: file });
                  }}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  PNG, JPG, or SVG. Recommended 200x200px. Max 2MB.
                </p>
              </div>
              <Button onClick={handleCreateRetailer} disabled={createRetailerMutation.isPending}>
                {createRetailerMutation.isPending ? 'Creating...' : 'Create Retailer'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isEditRetailerDialogOpen} onOpenChange={setIsEditRetailerDialogOpen}>
          <DialogContent className={cn('bg-white')}>
            <DialogHeader>
              <DialogTitle>Edit Retailer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="editName">Retailer Name</Label>
                <Input
                  id="editName"
                  value={retailerFormData.name || ''}
                  onChange={(e) =>
                    setRetailerFormData({ ...retailerFormData, name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="editDescription">Description</Label>
                <Textarea
                  id="editDescription"
                  value={retailerFormData.description || ''}
                  onChange={(e) =>
                    setRetailerFormData({ ...retailerFormData, description: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="editLogo">Logo</Label>
                <Input
                  id="editLogo"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) {
                      toast({
                        title: 'File too large',
                        description: 'Logo must be under 2MB',
                        variant: 'destructive',
                      });
                      return;
                    }
                    setRetailerFormData({ ...retailerFormData, logoFile: file });
                  }}
                />
                {editingRetailer?.logo && (
                  <div className="mt-2">
                    <p className="mb-1 text-xs text-muted-foreground">Current logo:</p>
                    <img
                      src={editingRetailer.logo}
                      alt="Current logo"
                      className="h-12 w-auto rounded border object-contain"
                    />
                  </div>
                )}
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">
                      {editingRetailer?.archivedAt ? 'Restore Retailer' : 'Archive Retailer'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {editingRetailer?.archivedAt
                        ? 'Restore this retailer to the active directory.'
                        : 'Archive to hide from the active directory. Can be restored later.'}
                    </p>
                  </div>
                  <Button
                    variant={editingRetailer?.archivedAt ? 'outline' : 'secondary'}
                    size="sm"
                    onClick={() => {
                      if (editingRetailer) {
                        setRetailerToArchive(editingRetailer);
                        setIsArchiveDialogOpen(true);
                      }
                    }}
                    disabled={archiveRetailerMutation.isPending}
                  >
                    {editingRetailer?.archivedAt ? (
                      <>
                        <ArchiveRestore className="mr-1 h-4 w-4" /> Restore
                      </>
                    ) : (
                      <>
                        <Archive className="mr-1 h-4 w-4" /> Archive
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsEditRetailerDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateRetailer}
                  disabled={updateRetailerMutation.isPending}
                >
                  {updateRetailerMutation.isPending ? 'Updating...' : 'Update Retailer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent className={cn('bg-white')}>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete &quot;{retailerToDelete?.name}&quot; and all its
                associated data. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (retailerToDelete) deleteRetailerMutation.mutate(retailerToDelete.id);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteRetailerMutation.isPending}
              >
                {deleteRetailerMutation.isPending ? 'Deleting...' : 'Delete Retailer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
          <AlertDialogContent className={cn('bg-white')}>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {retailerToArchive?.archivedAt ? 'Restore Retailer?' : 'Archive Retailer?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {retailerToArchive?.archivedAt
                  ? `This will restore "${retailerToArchive?.name}" and make it visible in the active retailer list again.`
                  : `This will archive "${retailerToArchive?.name}". It will be hidden from the active retailer list but can be restored later.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (retailerToArchive) {
                    archiveRetailerMutation.mutate({
                      id: retailerToArchive.id,
                      archive: !retailerToArchive.archivedAt,
                    });
                  }
                }}
                disabled={archiveRetailerMutation.isPending}
              >
                {archiveRetailerMutation.isPending
                  ? retailerToArchive?.archivedAt
                    ? 'Restoring...'
                    : 'Archiving...'
                  : retailerToArchive?.archivedAt
                    ? 'Restore'
                    : 'Archive'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </RetailerWorkspaceProvider>
  );
}
